import { z } from 'zod';

import { EmailStringSchema } from '../../common';
import { makeEndpoint, paginateResponseSchema, PhoneNumberValidationSchema, sortOptions } from '../common';

export enum BrandMembershipRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  HOST = 'host',
}

export const BrandMembershipRolesSchema = z.nativeEnum(BrandMembershipRole);

export const UserAddressSchema = z
  .object({
    street: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  })
  .partial();

export const UserSchema = z.object({
  createdAt: z.string().datetime(),
  firstName: z.string(),
  lastName: z.string(),
  emailAddress: EmailStringSchema,
  phoneNumber: z.string().nullable(),
  role: z.nativeEnum(BrandMembershipRole).nullable(),
  title: z.string().nullable(),
  id: z.string().uuid(),
  lastAccessedAt: z.string().datetime().nullable(),
  profilePictureId: z.string().uuid().nullable(),
  countryCode: z.string().nullable(),
});

export enum UserImageSizes {
  W48 = '48',
  W256 = '256',
}

export const UserImageSizeInUriSchema = z.object(
  Object.fromEntries(Object.values(UserImageSizes).map((size: UserImageSizes) => [`${size}w`, z.string()])) as {
    [key in `${UserImageSizes}w`]: z.ZodString;
  },
);
export const UserImageSizeWithOriginalInUriSchema = UserImageSizeInUriSchema.extend({ original: z.string() });
export const UserImageSizeMediaSchema = z.object({ id: z.string().uuid(), uri: UserImageSizeWithOriginalInUriSchema });

export const UserResponseSchema = UserSchema.omit({ profilePictureId: true }).extend({
  profilePicture: UserImageSizeMediaSchema.nullable(),
});

export type UserType = z.infer<typeof UserSchema>;

export const PaginatedUsersResponseSchema = paginateResponseSchema(UserResponseSchema);
export type PaginatedUsersResponseType = z.infer<typeof PaginatedUsersResponseSchema>;

export enum PaymentPlatform {
  STRIPE_US = 'stripe-us',
  STRIPE_EU = 'stripe-eu',
  KICC = 'kicc',
}

export enum Status {
  DRAFT = 'draft',
  PUBLISHED = 'published',
}

export enum Role {
  CUSTOMER = 'customer',
  BRAND_USER = 'brand-user',
  STAFF_ADMIN = 'staff-admin',
  INTEGRATION = 'integration',
  SYSTEM = 'system',
  ORG_USER = 'org-user',
}

export const BrandSchema = z.object({
  id: z.string(),
  name: z.string(),
  currency: z.string(),
  paymentPlatform: z.nativeEnum(PaymentPlatform),
});

export const MembershipSchema = z.object({
  brand: BrandSchema,
  id: z.string().uuid(),
  role: z.nativeEnum(BrandMembershipRole),
  isConfirmed: z.boolean(),
  permissions: z.array(z.string()).nullable(),
  lastAccessedAt: z.string().datetime().nullable(),
});

export const UserDetailSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  emailAddress: EmailStringSchema,
  phoneNumber: z.string().nullable(),
  address: UserAddressSchema.nullable(),
  role: z.nativeEnum(Role),
  title: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  description: z.string().nullable(),
  memberships: z.array(MembershipSchema),
  confirmedAt: z.string().datetime().nullable(),
  isResetPassword: z.boolean().nullable(),
  profilePicture: z
    .object({
      id: z.string(),
      uri: z.object({ original: z.string() }).and(z.record(z.string())),
    })
    .nullable(),
  countryCode: z.string().nullable(),
});

export type UserDetailType = z.infer<typeof UserDetailSchema>;

export enum UserSortBy {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  LAST_ACCESS_AT = 'lastAccessedAt',
}

export const GetUsersQuerySchema = z
  .object({
    q: z.string().optional(),
    sortBy: z.nativeEnum(UserSortBy).optional(),
    sortDir: z.enum(sortOptions).optional(),
    page: z.coerce.number().min(1).default(1),
    limit: z.coerce.number().min(1).default(10),
  })
  .strict();

export const UserRequestSchema = z
  .object({
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: EmailStringSchema,
    phoneNumber: PhoneNumberValidationSchema.nullable(),
    dateOfBirth: z.coerce.date(),
    description: z.string().nullable(),
    profilePictureId: z.string().uuid().nullable(),
    role: z.nativeEnum(Role),
    title: z.string().nullable(),
    countryCode: z.string().nullable(),
    allowOverlappingBookings: z.boolean(),
    auth0Id: z.string().uuid().nullable().optional(),
    acceptedTermsAndConditions: z.boolean().nullable(),
    address: UserAddressSchema,
    socialMediaHandle: z
      .object({
        instagram: z.string(),
        twitter: z.string(),
      })
      .partial(),
    verifyBrands: z.array(
      z.object({
        brandId: z.string(),
        verify: z.boolean(),
      }),
    ),
  })
  .partial();

export const ChangePasswordSchema = z.object({
  currentPassword: z.string(),
  newPassword: z.string().min(6),
});

export const ResetPasswordSchema = z.object({
  password: z.string().min(6),
});

export const EmailAddressSchema = z.object({
  emailAddress: EmailStringSchema,
  role: z.nativeEnum(Role),
  skipId: z.string().uuid(),
});

export const PhoneNumberSchema = z.object({
  phoneNumber: PhoneNumberValidationSchema,
});

export const UpdateUserCountryCodeRequestSchema = z.object({
  countryCode: z.string().nullable(),
});

export const UpdateUserCountryCodeEndpoint = makeEndpoint({
  method: 'PATCH',
  url: () => `/v1/users/me/country-code`,
  requestSchema: UpdateUserCountryCodeRequestSchema,
  responseSchema: z.void(),
});

export enum UserField {
  DESCRIPTION = 'description',
}

const GetHostsResponseSchema = z.array(
  z.object({
    id: z.string().uuid(),
    firstName: z.string(),
    lastName: z.string(),
    description: z.string().nullable(),
    profilePicture: z
      .object({
        id: z.string().uuid(),
        uri: UserImageSizeWithOriginalInUriSchema,
      })
      .nullable(),
  }),
);

const GetHostsQuerySchema = z.object({
  userIds: z.array(z.string().uuid()),
  language: z.string().optional(),
});

export const GetHostsEndpoint = makeEndpoint({
  method: 'GET',
  url: () => `/v1/users/hosts`,
  requestSchema: z.void(),
  responseSchema: GetHostsResponseSchema,
  querySchema: GetHostsQuerySchema,
});

export const GetBrandAssociationsEndpoint = makeEndpoint({
  method: 'GET',
  url: (params: { [key: string]: unknown; query: { email: string } }) =>
    `/v1/users/brand-associations?email=${encodeURIComponent(params.query.email)}`,
  requestSchema: z.void(),
  querySchema: z.object({ email: z.string() }),
  responseSchema: z.object({ brandsCount: z.number(), message: z.string() }),
});
